import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import api from '../lib/api';

export default function AvailabilityFormPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [interested, setInterested] = useState(null);
  const [callType, setCallType] = useState('');
  const [contactPref, setContactPref] = useState('');
  const [slots, setSlots] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/public/recruitment/availability/${token}`);
        setData(r.data);
        const resp = r.data.response;
        if (resp) {
          setInterested(resp.interested);
          setCallType(resp.call_type || '');
          setContactPref(resp.contact_pref || '');
          setSlots(resp.slots || []);
        }
      } catch (e) {
        setError('Link non valido o scaduto.');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const toggleSlot = (id) => {
    setSlots((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  };

  const submit = async () => {
    if (interested === null) return;
    if (interested && (!callType || !contactPref || slots.length === 0)) {
      alert('Compila tutti i campi: tipo di colloquio, come preferisci essere contattato/a e almeno una fascia oraria.');
      return;
    }
    setSaving(true);
    try {
      await api.post(`/public/recruitment/availability/${token}`, {
        interested, call_type: callType, contact_pref: contactPref, slots,
      });
      setSubmitted(true);
    } catch (e) {
      alert(e.response?.data?.detail || 'Errore durante l\'invio, riprova.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-400">Caricamento...</div>;
  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <Card className="max-w-md w-full"><CardContent className="p-6 text-center text-slate-600">{error}</CardContent></Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-[#1a4b8c] text-white">
        <div className="max-w-xl mx-auto px-4 py-6 flex items-center gap-3">
          <img src="/logo.svg" alt="Metal Covering" className="h-10 w-10 shrink-0 brightness-0 invert" />
          <div><h1 className="text-xl font-bold">Metal Covering S.r.l.</h1><p className="text-sm text-blue-100">Disponibilità candidato</p></div>
        </div>
      </div>

      <div className="max-w-xl mx-auto px-4 py-8">
        {submitted ? (
          <Card><CardContent className="p-8 text-center space-y-2">
            <p className="text-lg font-semibold text-slate-800">Grazie{data.name ? `, ${data.name}` : ''}!</p>
            <p className="text-sm text-slate-500">Le tue risposte sono state registrate. Ti ricontatteremo a breve.</p>
          </CardContent></Card>
        ) : (
          <Card><CardContent className="p-6 space-y-6">
            <div>
              <p className="text-lg font-semibold text-slate-800">Ciao {data.name || ''},</p>
              <p className="text-sm text-slate-600 mt-1">
                grazie per la tua candidatura{data.positions?.length ? ` per ${data.positions.join(', ')}` : ''} presso Metal Covering S.r.l.
                Ci servono 30 secondi per sapere come procedere.
              </p>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-semibold">Sei ancora interessato/a alla posizione?</Label>
              <div className="flex gap-2">
                <Button type="button" variant={interested === true ? 'default' : 'outline'} className={interested === true ? 'bg-[#1a7d3c] hover:bg-[#166332]' : ''} onClick={() => setInterested(true)} data-testid="interested-yes">Sì, sono interessato/a</Button>
                <Button type="button" variant={interested === false ? 'default' : 'outline'} className={interested === false ? 'bg-slate-600 hover:bg-slate-700' : ''} onClick={() => setInterested(false)} data-testid="interested-no">No, non più</Button>
              </div>
            </div>

            {interested === true && (
              <>
                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Preferisci un colloquio in videochiamata o una chiamata telefonica normale?</Label>
                  <RadioGroup value={callType} onValueChange={setCallType} className="grid-flow-col auto-cols-max gap-4" data-testid="call-type">
                    <div className="flex items-center gap-2"><RadioGroupItem value="video" id="ct-video" /><Label htmlFor="ct-video" className="font-normal">Videochiamata</Label></div>
                    <div className="flex items-center gap-2"><RadioGroupItem value="telefonica" id="ct-tel" /><Label htmlFor="ct-tel" className="font-normal">Chiamata telefonica</Label></div>
                  </RadioGroup>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Preferisci essere ricontattato/a via email o telefono?</Label>
                  <RadioGroup value={contactPref} onValueChange={setContactPref} className="grid-flow-col auto-cols-max gap-4" data-testid="contact-pref">
                    <div className="flex items-center gap-2"><RadioGroupItem value="email" id="cp-email" /><Label htmlFor="cp-email" className="font-normal">Email</Label></div>
                    <div className="flex items-center gap-2"><RadioGroupItem value="telefono" id="cp-tel" /><Label htmlFor="cp-tel" className="font-normal">Telefono</Label></div>
                  </RadioGroup>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Quando sei disponibile? (seleziona una o più fasce)</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {(data.slots || []).map((s) => (
                      <div key={s.id} className="flex items-center gap-2 border rounded-md px-3 py-2">
                        <Checkbox checked={slots.includes(s.id)} onCheckedChange={() => toggleSlot(s.id)} id={`slot-${s.id}`} data-testid={`slot-${s.id}`} />
                        <Label htmlFor={`slot-${s.id}`} className="font-normal text-sm">{s.label}</Label>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            <Button className="w-full bg-[#1a4b8c] hover:bg-[#153d70]" disabled={interested === null || saving} onClick={submit} data-testid="submit-availability">
              {saving ? 'Invio in corso...' : 'Invia risposta'}
            </Button>
          </CardContent></Card>
        )}
      </div>
    </div>
  );
}
